'use client';

import clsx from 'clsx';
import { motion } from 'framer-motion';
import { CircleX, Maximize2, Minimize2 } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { useFreezeScroll } from '@/hooks/use-freeze-scroll';
import { useKeydown } from '@/hooks/use-keydown';

import { confirmWithDialog, ConfirmWithDialogArgs } from './confirm-dialog';
import { Portal } from './portal';
import { Button } from './ui/button';

const DRAWER_VIEWPORT_ID = 'drawer-viewport';

export type DrawerProps = {
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  confirmClose?: ConfirmWithDialogArgs;
  defaultOpen?: boolean;
  disabledFooterToggle?: boolean;
  drawerRef?: React.RefObject<HTMLDivElement>;
  // Pinned between the title and the scrolling content, e.g. a progress indicator.
  header?: React.ReactNode;
  onClose?: () => void;
  onConfirmCloseCancel?: () => void;
  onOpen?: () => void;
  scrim?: boolean;
  title: string;
  trigger: React.ReactNode;
  triggerClassName?: string;
  allowFullScreen?: boolean;
  defaultFullScreen?: boolean;
  fullScreen?: boolean;
};

export function Drawer({
  actions,
  children,
  className,
  confirmClose,
  defaultOpen = false,
  disabledFooterToggle = false,
  drawerRef,
  header,
  onClose,
  onConfirmCloseCancel,
  onOpen,
  scrim = false,
  title,
  trigger,
  triggerClassName,
  allowFullScreen = false,
  defaultFullScreen = false,
  fullScreen = false,
}: DrawerProps) {
  const initialized = useRef(false);
  const wrapperId = useId();
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [isFullScreen, setIsFullScreen] = useState(defaultFullScreen || fullScreen);
  const isOpenDebounced = useDebouncedValue(isOpen, { millis: 1000 });
  const isRendered = isOpen || isOpenDebounced;
  const motionState = isOpen ? 'open' : 'closed';
  const showScrim = useDebouncedValue(scrim && isOpen, { millis: 50 });
  const toggle = useCallback(() => {
    setIsOpen(!isOpen);

    if (isOpen && onClose) {
      onClose();
    }
  }, [isOpen, onClose]);
  const onKeydown = useCallback(
    (e: Event) => {
      const event = e as KeyboardEvent;

      event.key === 'Escape' && toggle();
    },
    [toggle]
  );

  useFreezeScroll(isOpen);

  useKeydown({ isActive: isOpen, callback: onKeydown });

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      defaultOpen && onOpen?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsFullScreen(defaultFullScreen || fullScreen);
  }, [defaultFullScreen, fullScreen]);

  return (
    <>
      <div
        className={triggerClassName}
        onClick={() => {
          onOpen?.();
          toggle();
        }}
      >
        {trigger}
      </div>

      <Portal id={DRAWER_VIEWPORT_ID}>
        {isRendered && (
          <section
            className={clsx(
              `drawer-is-${motionState}`,
              `drawer-scrim-${showScrim ? 'show' : 'hide'}`,
              'fixed top-0 right-0 z-10 h-[100dvh] max-w-full'
            )}
          >
            <motion.div
              animate={motionState}
              className={clsx(
                'drawer-content bg-primary text-primary-foreground h-full transition-all duration-300',
                isFullScreen
                  ? 'w-[100vw] sm:w-[100vw] md:w-[100vw] border-0 sm:rounded-none'
                  : 'w-[100vw] sm:w-[calc(75vw)] md:w-[30rem] border-2 sm:rounded-l-xl'
              )}
              initial={{ x: 'calc(100%)' }}
              variants={{ open: { x: 0 }, closed: { x: 'calc(100%)' } }}
            >
              <div
                className={clsx('wrapper', 'flex flex-col h-full p-4', className)}
                id={wrapperId}
                ref={drawerRef}
              >
                {allowFullScreen && (
                  <Button
                    className='drawer-fullscreen-button absolute top-3 right-16'
                    disabled={!isOpen}
                    name={isFullScreen ? 'minimize' : 'maximize'}
                    onClick={() => setIsFullScreen(!isFullScreen)}
                    variant='ghost'
                    size='icon'
                  >
                    {isFullScreen ? (
                      <Minimize2 className='h-5 w-5' />
                    ) : (
                      <Maximize2 className='h-5 w-5' />
                    )}
                  </Button>
                )}

                <Button
                  autoFocus
                  className='drawer-close-button absolute top-3 right-3'
                  disabled={!isOpen}
                  name='close'
                  onClick={async () => {
                    const confirmed = confirmClose ? await confirmWithDialog(confirmClose) : true;

                    if (confirmed) {
                      toggle();
                    } else if (onConfirmCloseCancel) {
                      onConfirmCloseCancel();
                    }
                  }}
                  variant='ghost'
                  size='icon'
                >
                  <CircleX className='h-5 w-5' />
                </Button>

                <div className={clsx('p-4', allowFullScreen ? 'pr-28' : 'pr-16')}>
                  <h3 className='text-xl font-bold'>{title}</h3>
                </div>
                {header && <div className='shrink-0'>{header}</div>}
                <div className='content hidden-scroll min-h-0 flex-1 p-4'>{children}</div>
                {/*
                  Pinned action bar. The `h-[100dvh]` above is what keeps it on screen: a
                  `100vh`/`bottom-0` box is measured against the *large* viewport, so on mobile the
                  last row slides under the browser toolbar and takes Claim/Save/Unclaim with it.
                  Providers reported exactly that — the button was there, just never reachable.
                  Only rendered when a drawer supplies actions: the border and background would
                  otherwise draw an empty bar under the drawers that pass none.
                */}
                {actions && (
                  <div
                    className='actions toggle shrink-0 flex flex-row-reverse gap-4 border-t border-primary-foreground/15 bg-primary pt-3 pb-[max(0.25rem,env(safe-area-inset-bottom))]'
                    onClick={() => !disabledFooterToggle && toggle()}
                  >
                    {actions}
                  </div>
                )}
              </div>
            </motion.div>
          </section>
        )}
      </Portal>
    </>
  );
}

export function closeHighestDrawer() {
  const buttons = document.getElementById(DRAWER_VIEWPORT_ID)?.querySelectorAll('.drawer-close-button');
  const lastButton = buttons && buttons[buttons.length - 1];

  if (lastButton) {
    (lastButton as HTMLElement).click();
  }
}

export function DrawerViewport() {
  return (
    <div id={DRAWER_VIEWPORT_ID}>
      <div className='drawer-viewport-overlay fixed inset-0 z-10' onClick={closeHighestDrawer} />
    </div>
  );
}
